import React, { memo, useCallback } from 'react'
import { DomesticCartLeisureOption } from '~/api/converters/domestic/cart/ProductSection/Leisure/index.type'
import { cn, Checkbox, Stepper, showToast } from '@yanolja-rnd/nol-design-system'
import { ProductBadge } from '@yanolja-rnd/nol-design-system'
import PrimaryProductBadges from '~/app/components-nds/domestic/common/primary-product-badges'
import { toKRW } from '~/app/utils/krNumberToString'
import ProductThumbnail from '~/app/components-nds/common/thumbnail'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import useDomesticCartOptionUpdate from '~/app/components-nds/domestic/cart/hooks/use-demestic-cart-option-update'
import { useDomesticCartQuery } from '~/app/react-query/domestic/cart'
import CancellationPolicy from '~/app/components-nds/common/cancellation-policy'
import ProductOutdated from '~/app/components-nds/common/product-outdated'
import OptionDeleteButton from '~/app/components-nds/domestic/cart/cart-product-section/items/common/option-delete-button'
/**
 * LeisureItem 컴포넌트 Props 타입
 */
interface LeisureOptionProps {
  option: DomesticCartLeisureOption
  onTitleClick?: () => void
}

/**
 * 상품 정보 섹션 컴포넌트
 */
const ProductInfo = memo<{
  subTitle: string
  validateDuration: string
  memberOnly: boolean
  disabledSale: boolean
}>(({ subTitle, validateDuration, memberOnly, disabledSale }) => (
  <div className={cn('flex flex-col gap-6', disabledSale && 'text-text-neutral-medium')}>
    {subTitle && <div className={cn('typography-subtitle-16-bold')}>{subTitle}</div>}
    <div className={cn('typography-body-14-regular-multiline')}>{validateDuration}</div>
    {memberOnly && (
      <ProductBadge
        type="outlined_primary"
        size="medium"
        className="w-fit px-6 py-2"
        aria-label="로그인 회원 전용 상품"
      >
        로그인 회원 전용 상품
      </ProductBadge>
    )}
  </div>
))

/**
 * 가격 컴포넌트
 */
const ProductPrice = memo<{ price: number; quantity: number; isDisabled: boolean }>(
  ({ price, quantity, isDisabled }) => {
    const priceText = toKRW(price * quantity)
    return (
      <div
        className={cn(
          'text-right typography-subtitle-16-bold pc:typography-subtitle-18-bold',
          isDisabled && 'text-text-neutral-medium line-through'
        )}
      >
        {priceText}
      </div>
    )
  }
)

/**
 * 레저 상품 아이템 컴포넌트
 *
 * @description 장바구니에 표시되는 레저 상품 아이템을 렌더링하는 컴포넌트
 * @param item - 레저 상품 데이터
 */
const LeisureOption: React.FC<LeisureOptionProps> = memo(({ option, onTitleClick }) => {
  const { refetch } = useDomesticCartQuery()
  const { updateCartOption } = useDomesticCartOptionUpdate()

  const {
    optionId,
    optionTitle,
    optionSubTitle,
    imageUrl,
    periodText,
    price,
    quantity,
    maxQuantity,
    primaryBadges,
    isMemberOnly,
    almostSoldOut,
    isSaleAvailable,
    disabledText,
    cancellationPolicy,
    needUpdate,
  } = option

  // 장바구니 상태 관리
  const { isSelected, selectOption, unselectOption } = useDomesticCartStore(
    ({ selectedOptionIds, selectOption, unselectOption }) => {
      return {
        isSelected: selectedOptionIds.has(optionId),
        selectOption,
        unselectOption,
      }
    }
  )

  // 체크박스 변경 핸들러
  const handleCheckboxChange = () => {
    if (isSelected) {
      unselectOption(optionId)
    } else {
      selectOption(optionId)
    }
  }

  /**
   * 최대 구매 수량 초과 토스트 메시지 표시
   */
  const showMaxQuantityToast = useCallback(() => {
    showToast({
      clearOtherToast: true,
      children: (
        <>
          이 상품은 최대 {maxQuantity}개까지
          <br />
          구매할 수 있어요
        </>
      ),
    })
  }, [maxQuantity])

  const updateLeisureOptionQuantity = useCallback(
    (quantity: number) => {
      updateCartOption(
        {
          optionId,
          domesticLeisure: {
            quantity,
          },
        },
        {
          onError: error => {
            if (error.response?.data?.code === 'CART_MODIFY_REQUEST_001') {
              showMaxQuantityToast()
            }
          },
        }
      )
    },
    [optionId, updateCartOption, showMaxQuantityToast]
  )

  const onInvalidRange = (value: number | string) => {
    if (Number(value) > maxQuantity) {
      showMaxQuantityToast()
      return
    }
  }

  return (
    <div className="flex flex-col gap-16" aria-label={`${optionTitle} 상품`}>
      {/* 체크박스 헤더 */}
      <div className="flex flex-col gap-8">
        {!!primaryBadges?.length && <PrimaryProductBadges badges={primaryBadges} />}
        <div className="flex items-start gap-8">
          <div className="flex w-full items-start gap-6">
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={!isSaleAvailable}
              onChange={handleCheckboxChange}
              aria-label={`${optionTitle} 선택`}
            />
            <div
              className={cn(
                'typography-subtitle-16-bold hover:cursor-pointer',
                !isSaleAvailable && 'text-text-neutral-medium'
              )}
              onClick={onTitleClick}
            >
              {optionTitle}
            </div>
          </div>
          <OptionDeleteButton optionId={optionId} aria-label={`${optionTitle} 삭제`} />
        </div>
      </div>

      {/* 아이템 컨텐츠 영역 */}
      <div className="flex items-start gap-12">
        <ProductThumbnail
          image={imageUrl}
          isDisabled={!isSaleAvailable}
          className="h-auto min-h-118 max-w-88 pc:max-w-108"
          alt={optionTitle}
        />
        <div className="flex w-full flex-col">
          <ProductInfo
            subTitle={optionSubTitle}
            validateDuration={periodText}
            memberOnly={isMemberOnly}
            disabledSale={!isSaleAvailable}
          />

          {/* 가격 및 수량 선택 영역 */}
          <div className="mt-12 flex flex-col items-end gap-8">
            <div className="flex flex-col items-end gap-4">
              <ProductPrice price={price} quantity={quantity} isDisabled={!isSaleAvailable} />
              {almostSoldOut && (
                <div className="text-text-warning-main typography-body-12-bold">품절임박</div>
              )}
              {!isSaleAvailable && (
                <div className="text-text-neutral-main typography-body-12-bold">{disabledText}</div>
              )}
            </div>
            <Stepper
              value={quantity}
              onChange={updateLeisureOptionQuantity}
              min={1}
              max={maxQuantity}
              onInvalidRange={onInvalidRange}
              disabled={!isSaleAvailable}
              aria-label={`${optionTitle} 수량 조절`}
            />
          </div>
        </div>
      </div>
      {!!cancellationPolicy &&
        (cancellationPolicy.type === 'policy-error' ? (
          <CancellationPolicy {...cancellationPolicy} onRefresh={() => refetch()} />
        ) : (
          <CancellationPolicy {...cancellationPolicy} />
        ))}
      {/* 상품 정보 변경 영역 */}
      {needUpdate && <ProductOutdated />}
    </div>
  )
})

export default LeisureOption
